import { DONOR_PII_QUERY_PARAMS, isReceiptPath, scrubCaptureResult, scrubPortalCaptureResult } from './urlPrivacy.js';

// Safe environment variable access for non-Vite/non-ESM compatibility
const getEnv = (key, fallback = undefined) => {
  try {
    // Use nullish coalescing to preserve falsy values (empty string, 0, false)
    const value = typeof import.meta !== 'undefined' ? import.meta.env?.[key] : undefined;
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

// Special helper for DEV mode detection (import.meta.env.DEV is a boolean property, not string key)
const isDev = () => {
  try {
    return typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
  } catch {
    return false;
  }
};

const POSTHOG_DIRECT_API_HOST = 'https://us.i.posthog.com';
const POSTHOG_DIRECT_UI_HOST = 'https://us.posthog.com';

const trimTrailingSlash = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\/+$/, '');
};

const getFirstEnv = (...keys) => {
  for (const key of keys) {
    const value = getEnv(key);
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const inferPostHogUiHost = (apiHost) => {
  if (apiHost?.includes('eu.i.posthog.com')) return 'https://eu.posthog.com';
  return POSTHOG_DIRECT_UI_HOST;
};

export const getPostHogApiHost = () =>
  trimTrailingSlash(
    getFirstEnv(
      'VITE_POSTHOG_PROXY_HOST',
      'VITE_PUBLIC_POSTHOG_PROXY_HOST',
      'VITE_POSTHOG_HOST',
      'VITE_APP_PUBLIC_POSTHOG_HOST',
    ) || POSTHOG_DIRECT_API_HOST,
  );

export const getPostHogUiHost = (apiHost = getPostHogApiHost()) =>
  trimTrailingSlash(getFirstEnv('VITE_POSTHOG_UI_HOST') || inferPostHogUiHost(apiHost));

const defaultPostHogHost = getPostHogApiHost();
const defaultPostHogUiHost = getPostHogUiHost(defaultPostHogHost);
const shouldEnablePostHogDebug = () => getEnv('VITE_POSTHOG_DEBUG', 'false') === 'true';
const appsWithLiveFeatureFlags = new Set(['dashboard']);

export const getPostHogConfig = (appName) => ({
  // PostHog SDK expects snake_case
  api_host: defaultPostHogHost,
  ui_host: defaultPostHogUiHost,
  // Typings and consumers expect camelCase (index.d.ts)
  apiHost: defaultPostHogHost,
  uiHost: defaultPostHogUiHost,

  // Cost-efficient: Only create profiles for identified users
  person_profiles: 'identified_only',

  // Autocapture and SPA support with modern defaults (2025-05-24 includes history_change for React SPAs)
  defaults: '2025-05-24',
  autocapture: false,
  capture_pageleave: false,
  // Disable $web_vitals — not referenced in any insight or dashboard
  capture_performance: { web_vitals: false },
  // Only dashboard currently uses PostHog feature flags in live code.
  // Disable feature-flag evaluation in public apps while keeping analytics active.
  // We intentionally do not set advanced_disable_flags here because that broader
  // switch also disables remote config used by other PostHog SDK features.
  advanced_disable_feature_flags: !appsWithLiveFeatureFlags.has(appName),

  // PostHog Surveys are not used anywhere in this repo and are switched OFF at the
  // PostHog project level (remote config `/array/<key>/config` returns
  // `"surveys": false` — verified 2026-09-02). Without this flag the SDK still
  // lazy-loads its ~100KB survey renderer from `<api_host>/static/surveys.js` on
  // every page load, including the checkout payment page. A truncated fetch of that
  // script threw `SyntaxError: Unexpected EOF` on Mobile Safari 16.6 and paged as a
  // HIGH-priority first-party error, because we reverse-proxy PostHog on our own
  // domain (s.charitystack.com) so its code is indistinguishable from ours to
  // Sentry's in-app heuristic. `packages/website` already sets this; the shared
  // config did not, so the other five apps kept paying for it.
  // Unlike `advanced_disable_flags` (see the note above), this switch is narrow —
  // it only blocks the surveys dependency and leaves remote config intact.
  // If a survey is ever wanted, enable it in PostHog AND remove this line.
  // Sentry issue: CHARITYSTACK-MONOREPO-PF (PE-2116)
  disable_surveys: true,

  // CON-261: a portal receipt address carries a bearer code in its path (and a
  // signature in `t`). `before_send` scrubs events, but a session replay's page
  // metadata records the raw URL, so the portal does not record a page load that
  // starts on a receipt. Read once, at init.
  ...(appName === 'portal' && typeof window !== 'undefined' && isReceiptPath(window.location?.pathname)
    ? { disable_session_recording: true }
    : {}),

  // Personal data masking configuration (required by PostHog SDK to prevent undefined errors)
  // Fixes CHARITYSTACK-MONOREPO-5V: "ReferenceError: options is not defined"
  // These properties are used by PostHog's internal calculateEventProperties function
  mask_personal_data_properties: [
    'email',
    'phone',
    'name',
    'first_name',
    'last_name',
    'address',
    'city',
    'state',
    'zip',
    'postal_code',
    'country',
    'card_number',
    'cvv',
    'expiry',
  ],
  custom_personal_data_properties: [
    // CharityStack-specific PII fields
    'donor_email',
    'donor_name',
    'donor_phone',
    'donor_address',
    'billing_address',
    'shipping_address',
    // CON-154: the prefill query parameters a donor link can carry, bare (hosted
    // page) and `cs_`-prefixed (the widget iframe, forwarded by the embed script).
    // In posthog-js 1.282 `mask_personal_data_properties` is a BOOLEAN switch and
    // this list is what it masks in `$current_url` at capture time. The
    // `before_send` hook below is the belt to this brace: it also covers
    // `$referrer`, the `$initial_*` person properties and the `<formID>.field`
    // spelling, which this list cannot express.
    ...DONOR_PII_QUERY_PARAMS,
    ...DONOR_PII_QUERY_PARAMS.map((name) => `cs_${name}`),
  ],

  // CON-154: mask donor prefill values in every URL-shaped property before an
  // event leaves the page. Never drops an event; returns the input on any error.
  // `sanitize_properties` is deprecated in this SDK version; `before_send` is the
  // supported hook and sees the person-property bags too.
  // The portal also masks receipt codes on its own host (CON-261); every other
  // app keeps the shared scrub, so other sites' URLs are never rewritten.
  before_send: appName === 'portal' ? scrubPortalCaptureResult : scrubCaptureResult,

  // Session recording with privacy-first settings
  session_recording: {
    maskAllInputs: true,  // Mask all input fields by default
    maskTextSelector: '.ph-no-capture',  // Also mask elements with this class
    recordCrossOriginIframes: true,  // Enable for CharityStack cross-origin embeds
    recordCanvas: false,  // Disable canvas recording for performance
    
    // Block payment processor iframes to prevent cross-origin errors (FCD-956)
    // Stripe Elements, Finix, PayPal, Plaid iframes are cross-origin and should not be recorded
    blockSelector: [
      'iframe[name^="__privateStripeFrame"]',  // Stripe Elements iframes
      'iframe[src*="js.stripe.com"]',          // Stripe iframes
      'iframe[src*="hooks.stripe.com"]',       // Stripe hooks
      'iframe[src*="finix.com"]',              // Finix payment iframes
      'iframe[src*="paypal.com"]',             // PayPal iframes
      'iframe[src*="sandbox.paypal.com"]',     // PayPal sandbox
      'iframe[src*="plaid.com"]',              // Plaid Link iframes
      'iframe[src*="cdn.plaid.com"]',          // Plaid CDN
      'iframe[title="Secure card payment input frame"]', // Generic secure payment frames
    ].join(', '),
    
    // Comprehensive error handler for rrweb-record issues (FCD-956)
    // Prevents NS_ERROR_UNEXPECTED and other cross-origin errors from breaking session recording
    onError: (error) => {
      // List of errors to suppress (non-critical for session recording)
      const suppressibleErrors = [
        'NS_ERROR_UNEXPECTED',           // Firefox cross-origin iframe error
        'SecurityError',                  // Cross-origin security errors
        'Permission denied',              // Firefox permission errors
        'Blocked a frame',                // Chrome frame blocking
        'cross-origin',                   // Generic cross-origin errors
        'Failed to read',                 // Generic read failures
      ];
      
      // Extract error message from multiple possible locations for robust detection
      const errorStrings = [
        error?.message,
        error?.name,
        error?.stack,
        error?.cause?.message,
        error?.result,                   // Firefox-specific error result code
        error?.code,                     // Error code field
        String(error),
      ].filter(Boolean).join(' ');      // Combine all into searchable string
      
      const shouldSuppress = suppressibleErrors.some(msg => 
        errorStrings.includes(msg)
      );
      
      if (shouldSuppress) {
        // Suppress non-critical cross-origin errors (dev-only logging)
        if (isDev()) {
          console.warn('⚠️ PostHog: Suppressed cross-origin iframe error:', errorStrings.slice(0, 200));
        }
        return false; // Explicit false to suppress error per rrweb/PostHog contract
      }
      
      // Log unexpected errors for debugging (dev-only to avoid customer console spam)
      if (isDev()) {
        console.error('⚠️ PostHog session recording error:', error);
      }
      // In production, errors are suppressed to avoid noise in embedded contexts
      // PostHog's internal error handling will still report critical issues
      return false; // Always suppress to prevent breaking session recording
    },
  },

  // Iframe-specific config for donation-elements
  ...(appName === 'donation-elements' && {
    persistence: 'sessionStorage',  // Best compatibility for embedded widgets (works in 95%+ browsers)
    cross_subdomain_cookie: false,
    secure_cookie: true,
  }),

  // Register super properties (added to ALL events automatically)
  loaded: (posthog) => {
    posthog.register({
      app_name: appName,  // Differentiate events by package
      app_version: getEnv('VITE_APP_VERSION', 'unknown'),
    });

    // Enable debug logging in development
    if (isDev() && shouldEnablePostHogDebug()) {
      posthog.debug();
    }
  },
});

export const posthogConfig = getPostHogConfig;
